import type { CaseTypeKey } from '@core/designsystem';

/**
 * Фильтры списка дел: сначала состояние дела, потом его тип.
 *
 * Порядок не случаен: «что у меня в работе» юрист спрашивает чаще, чем «покажи все
 * консультации». Теговые чипы идут после этих — их набор задаёт сам юрист ([D-19]).
 *
 * **«Все» — это дела в работе, завершённые в них не входят.** Раньше эту роль играл
 * архив; партнёр разницы с «Завершено» не увидела, слово убрали, поведение оставили
 * ([D-38](docs/DECISIONS.md)).
 */
export type CasesFilter =
  | 'all'
  | 'active'
  | 'suspended'
  | 'completed'
  | 'consultation'
  | 'claim'
  | 'court'
  | 'enforcement'
  | 'other';

export const FILTERS: { key: CasesFilter; label: string }[] = [
  { key: 'all', label: 'Все' },
  { key: 'active', label: 'Активные' },
  { key: 'suspended', label: 'Приостановленные' },
  { key: 'completed', label: 'Завершённые' },
  { key: 'consultation', label: 'Консультации' },
  { key: 'claim', label: 'Претензионные' },
  { key: 'court', label: 'Судебные' },
  { key: 'enforcement', label: 'Исполнительные' },
  { key: 'other', label: 'Другие' },
];

/**
 * Порядок дел. По умолчанию «Срочность» ([D-13](docs/DECISIONS.md)): два контекста
 * тянут в разные стороны — «что горит» и «дело, с которым я работаю сейчас», —
 * и это правило складывает оба.
 */
export type CasesSort = 'urgency' | 'recent' | 'title' | 'manual';

export const SORTS: { key: CasesSort; label: string }[] = [
  { key: 'urgency', label: 'Срочность' },
  { key: 'recent', label: 'Недавние' },
  { key: 'title', label: 'По названию' },
  { key: 'manual', label: 'Свой порядок' },
];

/** Что подсвечивает строка срока: состояние дела либо просрочка. */
export type CaseAccent = 'active' | 'overdue' | 'paused' | 'completed';

/** Строка списка: готовый к показу текст, без форматирования в вёрстке. */
export interface CaseListItem {
  id: string;
  title: string;
  /** «Иванов И. И. · Судебное дело · банкротство» — клиент, тип и теги одной строкой. */
  subtitle: string;
  /** «Заседание 14 сен, 10:30» либо «Просрочено: подготовить претензию». */
  statusLine: string;
  accent: CaseAccent;
  typeAccent: CaseTypeKey;
  isPinned: boolean;
}

export interface TagFilter {
  id: string;
  name: string;
  isSelected: boolean;
}

export interface ClientOption {
  id: string;
  name: string;
}

export interface CasesState {
  isLoading: boolean;
  filter: CasesFilter;
  sort: CasesSort;
  cases: CaseListItem[];
  tagFilters: TagFilter[];
  clients: ClientOption[];
  clientFilterId: string | null;
  /** Режим перестановки: строки тянутся целиком, вверху «Готово» ([D-40]). */
  isReordering: boolean;
}
